import { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Polygon, useMapEvents } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { calculateFarmPolygon, getEfficiencyColor, getPowerOpacity } from '../utils/mapHelpers';
import './MapView.css';

// TODO: inject project-polygon-overlay when exact layout available
// The polygon overlay should be loaded from a GeoJSON file containing the exact
// farm boundary from planning application documents (DEFRA Magic Map / Island Green Power)

interface MapViewProps {
  lat: number;
  lon: number;
  areaAcres: number;
  currentPowerMW: number;
  peakPowerMW: number;
  efficiency: number;
  onLocationChange: (lat: number, lon: number) => void;
  onZoomChange?: (zoom: number) => void;
}

// Component to handle BEPS boundary overlay - simplified direct approach
function KMLOverlay({ map, showKML }: { map: L.Map | null; showKML: boolean }) {
  useEffect(() => {
    if (!map || !showKML) return;

    // Hardcoded bounds from the KML file (BEPS project boundary)
    const north = 52.5468814;
    const south = 52.4321741;
    const east = 1.4265126;
    const west = 1.1701835;

    const bounds = L.latLngBounds(
      L.latLng(south, west),
      L.latLng(north, east)
    );

    // Use the transparent PNG directly - no KML parsing, no MapWarper
    const imageUrl = 'assets/images/maps/BEPS.png';
    const imageOverlay = L.imageOverlay(imageUrl, bounds, {
      opacity: 0.85,
      interactive: false,
      attribution: 'BEPS Boundary Map'
    });

    imageOverlay.addTo(map);

    return () => {
      // Cleanup on unmount or when showKML changes
      if (map) {
        map.removeLayer(imageOverlay);
      }
    };
  }, [map, showKML]);

  return null;
}

// Component to handle map events
function MapEventHandler({
  onZoomChange,
  onLocationChange,
  farmCenter,
  setMap
}: {
  onZoomChange?: (zoom: number) => void;
  onLocationChange: (lat: number, lon: number) => void;
  farmCenter: [number, number];
  setMap: (map: L.Map) => void;
}) {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange?.(map.getZoom());
    },
    click: (e) => {
      // Allow repositioning farm with Shift+Click
      if (e.originalEvent.shiftKey) {
        onLocationChange(e.latlng.lat, e.latlng.lng);
      }
    },
  });

  // Set map instance for KML overlay
  useEffect(() => {
    setMap(map);
  }, [map, setMap]);

  // Center map on farm when location changes
  useEffect(() => {
    map.setView(farmCenter, map.getZoom(), { animate: true });
  }, [farmCenter, map]);

  return null;
}

// Component to animate the farm polygon
function AnimatedFarmPolygon({
  polygon,
  color,
  opacity,
  isDragging,
}: {
  polygon: LatLngExpression[];
  color: string;
  opacity: number;
  isDragging: boolean;
}) {
  return (
    <Polygon
      positions={polygon}
      pathOptions={{
        color: isDragging ? '#f59e0b' : color,
        fillColor: color,
        fillOpacity: opacity,
        weight: isDragging ? 3 : 2,
        dashArray: isDragging ? '5, 5' : undefined,
      }}
    />
  );
}

export function MapView({
  lat,
  lon,
  areaAcres,
  currentPowerMW,
  peakPowerMW,
  efficiency,
  onLocationChange,
  onZoomChange,
}: MapViewProps) {
  // BEPS KML boundary center coordinates
  const kmlCenterLat = 52.4895;
  const kmlCenterLon = 1.2983;
  const [visualMode, setVisualMode] = useState<'glossy' | 'gritty'>('glossy');
  const [currentZoom, setCurrentZoom] = useState(10);
  const [showKML, setShowKML] = useState(true);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);

  // Calculate farm polygon
  const farmPolygon = calculateFarmPolygon(lat, lon, areaAcres);

  // Calculate visual properties
  const color = getEfficiencyColor(efficiency);
  const opacity = getPowerOpacity(currentPowerMW, peakPowerMW);
  const isDragging = false; // TODO: implement drag functionality

  // Handle zoom change
  const handleZoomChange = useCallback((zoom: number) => {
    setCurrentZoom(zoom);
    onZoomChange?.(zoom);
  }, [onZoomChange]);

  // Tile layer URLs based on mode
  const tileUrl = visualMode === 'glossy'
    ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png';

  const tileAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

  return (
    <div className={`map-container ${visualMode}`}>
      <div className="map-controls">
        <div className="spacer"></div>
        <div className="mode-toggle">
          <button
            className={visualMode === 'glossy' ? 'active' : ''}
            onClick={() => setVisualMode('glossy')}
          >
            Glossy
          </button>
          <button
            className={visualMode === 'gritty' ? 'active' : ''}
            onClick={() => setVisualMode('gritty')}
          >
            Gritty
          </button>
        </div>
      </div>

      <MapContainer
        center={[kmlCenterLat, kmlCenterLon]}
        zoom={10}
        className="leaflet-map"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution={tileAttribution}
          url={tileUrl}
        />

        <KMLOverlay map={mapInstance} showKML={showKML} />

        {/* Farm polygon removed - using KML boundary instead */}

        <MapEventHandler
          onZoomChange={handleZoomChange}
          onLocationChange={onLocationChange}
          farmCenter={[kmlCenterLat, kmlCenterLon]}
          setMap={setMapInstance}
        />
      </MapContainer>

      <div className="map-legend">
        {showKML && (
          <div className="legend-item">
            <span className="legend-color" style={{ background: 'linear-gradient(to right, #ef4444, #f97316)', opacity: 0.8 }}></span>
            <span className="legend-label">BEPS Project Boundary ({areaAcres.toLocaleString()} acres)</span>
          </div>
        )}
        {!showKML && (
          <div className="legend-hint" style={{ color: '#64748b' }}>
            Toggle boundary overlay to see project area
          </div>
        )}
      </div>


    </div>
  );
}

